import React, { useState, useEffect } from "react";
import styles from "./Filter.module.css";
import RangeSlider from "../../../UI/RangeSlider/RangeSlider";
import SelectButton from "../../../UI/SelectButton/SelectButton";
import { FlatCardData } from "../../../CardsComponents/types";
import { calculateDiscountedPrice } from "../../../../utils/discountPriceUtils";
import cn from "classnames";

export type FilterSmallState = {
  type: string[];
  price_range: [number, number];
  floor_range: [number, number];
};

type FilterProps = {
  onApplyFilter: (filters: FilterSmallState) => void;
  onOpenFiltersModal: () => void;
  cardData: FlatCardData[];
  smallFilters: FilterSmallState;
  setSmallFilters: (filters: FilterSmallState) => void;
};

const Filter: React.FC<FilterProps> = ({
  onApplyFilter,
  onOpenFiltersModal,
  cardData,
  smallFilters,
  setSmallFilters,
}) => {
  const [filters, setFilters] = useState<FilterSmallState>({
    type: [],
    price_range: [0, 0],
    floor_range: [0, 0],
  });

  const [priceLimits, setPriceLimits] = useState<[number, number]>([0, 0]);
  const [floorLimits, setFloorLimits] = useState<[number, number]>([0, 0]);

  const formatNumber = (num: number): string =>
    num.toLocaleString("ru-RU").replace(/,/g, " ");

  const parseFormattedNumber = (value: string): number =>
    parseInt(value.replace(/\s+/g, ""), 10);

  useEffect(() => {
    const prices = cardData.map((card) => {
      const discountedPrice = calculateDiscountedPrice(
        card.pricePerM2 ?? "0",
        card.discountProperty ?? 0,
        card.price ?? "0"
      );
      return parseFormattedNumber(discountedPrice);
    });

    const floors = cardData.map((card) => card.floor);

    setPriceLimits([Math.min(...prices), Math.max(...prices)]);
    setFloorLimits([Math.min(...floors), Math.max(...floors)]);

    setFilters((prev) => ({
      ...prev,
      price_range: [Math.min(...prices), Math.max(...prices)],
      floor_range: [Math.min(...floors), Math.max(...floors)],
    }));
  }, [cardData]);

  const toggleType = (type: string) => {
    const updatedFilters = {
      ...smallFilters,
      type: smallFilters.type.includes(type)
        ? smallFilters.type.filter((t) => t !== type)
        : [...smallFilters.type, type],
    };
    setSmallFilters(updatedFilters);
    onApplyFilter(updatedFilters);
  };

  const updateRange = (key: keyof FilterSmallState, value: [number, number]) => {
    const updatedFilters = {
      ...smallFilters,
      [key]: value,
    };
    setSmallFilters(updatedFilters);
    onApplyFilter(updatedFilters);
  };

  useEffect(() => {
    onApplyFilter(filters);
  }, [filters, onApplyFilter]);

  return (
    <div className={styles.filter}>
      <div className={styles.filter_select_buttons}>
        <SelectButton
          label="Студия"
          isActive={smallFilters.type.includes("Студия")}
          onClick={() => toggleType("Студия")}
          theme="light"
        />
        <SelectButton
          label="1"
          isActive={smallFilters.type.includes("1-комнатная")}
          onClick={() => toggleType("1-комнатная")}
          theme="light"
        />
        <SelectButton
          label="2"
          isActive={smallFilters.type.includes("2-комнатная")}
          onClick={() => toggleType("2-комнатная")}
          theme="light"
        />
        <SelectButton
          label="3"
          isActive={smallFilters.type.includes("3-комнатная")}
          onClick={() => toggleType("3-комнатная")}
          theme="light"
        />
      </div>
      <div className={styles.range_slider_wrapper}>
        <RangeSlider
          min={priceLimits[0]}
          max={priceLimits[1]}
          value={smallFilters.price_range}
          onChange={(value) =>
            updateRange("price_range", value as [number, number])
          }
          formatValue={formatNumber}
          parseValue={parseFormattedNumber}
          label="₽"
          step={1000}
          theme="light"
        />
      </div>
      <div className={cn(styles.range_slider_wrapper, styles.hidden)}>
        <RangeSlider
          min={floorLimits[0]}
          max={floorLimits[1]}
          value={smallFilters.floor_range}
          onChange={(value) =>
            updateRange("floor_range", value as [number, number])
          }
          label="этажа"
          step={1}
          theme="light"
        />
      </div>
      <SelectButton onClick={onOpenFiltersModal} theme="light">
        Все фильтры
      </SelectButton>
    </div>
  );
};

export default Filter;